
import { useState, createContext, useContext, type ReactNode, useCallback } from 'react'
import { CurecallNotification, ECurecallNotificationAppearance, ECurecallNotificationSize } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import axios, { type AxiosError } from 'axios'

type TNotificationListItem = {
  id: string
  appearance: string
  size?: ECurecallNotificationSize
  title: string
  message: string
  timeoutInMs?: number
}
export type TNotification = {
  appearance: string
  size?: ECurecallNotificationSize
  title: string
  message: unknown
}

const NotificationContext = createContext<{
  notify: (notificationProps: TNotification, timeoutInMs?: number) => void
}>({ notify: () => {} })

export const useNotification = () => useContext(NotificationContext)

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notificationList, setNotificationList] = useState<TNotificationListItem[]>([])

  const { t } = useTranslation()

  const toggleNotification = (notificationId: string) => {
    setNotificationList((prev) => [...prev].filter((item) => item.id !== notificationId))
  }

  const notify = useCallback(
    (notificationProps: TNotification, timeoutInMs?: number) => {
      let message = t('common.error.generic')
      if (typeof notificationProps.message === 'string') {
        message = notificationProps.message
      } else if (axios.isAxiosError(notificationProps.message)) {
        const axiosError = notificationProps.message as AxiosError<{ message?: string }>
        message = axiosError?.response?.data?.message?.toString() || t('common.error.axios')
      }
      const newNotificationListItem: TNotificationListItem = {
        id: new Date().toISOString(),
        ...notificationProps,
        message,
        timeoutInMs,
      }
      setNotificationList((prev) => [...prev, newNotificationListItem])
    },
    [t],
  )

  return (
    <NotificationContext.Provider value={{ notify }}>
      <section className="c-fixed c-top-0 c-right-0 c-p-4 c-flex c-flex-col c-gap-2 c-z-[100]">
        {notificationList.map((item) => (
          <CurecallNotification
            key={item.id}
            id={item.id}
            appearance={item.appearance as ECurecallNotificationAppearance}
            size={item.size}
            title={item.title}
            message={item.message}
            timeoutInMs={item.timeoutInMs}
            onToggle={toggleNotification}
            closeLabel={t('common.actions.close')}
          />
        ))}
      </section>
      {children}
    </NotificationContext.Provider>
  )
}
